import qs from 'qs';
import { map, Observable } from 'rxjs';

import GatewayBase from './gateway-base';

export type YearlyMetrics = {
  year: number;
  orderCount: number;
  totalSales: number;
};

export type StoreMetricsResponse = {
  available: boolean;
  reason?: string;
  ordersPerYear?: YearlyMetrics[];
  averageOrderValue?: number;
  totalRevenue?: number;
  currency?: string;
  estimated?: boolean;
};

export type EmbeddedStatusResponse = {
  redirectUrl: string;
};

export enum CustomerDetailInclude {
  OtherBrands = 'otherBrands',
}

export type CustomerOtherBrands = { claimOrders: number; brands: number };

export type CustomerSummary = {
  addressCount: number;
  claimCount: number;
  insuredOrderCount: number;
  protectionSpendUsd: number;
  reimbursedUsd: number;
  insuredUsd: number;
};

export type CustomerDetailStore = { storeId: string; storeName: string };

export type CustomerDetailResponse = {
  customerId: string;
  storeId: string;
  storeName: string;
  email: string | null;
  name: string | null;
  firstOrderAt: string;
  dataAsOf: string | null;
  maxLagHours: number;
  pending: boolean;
  // The requested stores where the customer has orders, in request order.
  stores?: CustomerDetailStore[];
  otherBrands?: CustomerOtherBrands;
  // Present only with customer-rollup-views on and the customer settled.
  summary?: CustomerSummary;
};

export type CustomerShippingAddress = Record<
  'address1' | 'address2' | 'city' | 'stateAbr' | 'zip' | 'countryAbr',
  string | null
>;

export type CustomerHistoryOrder = {
  orderId: string;
  storeId: string;
  storeName: string | null;
  orderNumber: string | null;
  protected: boolean;
  claimType: string | null;
  claimState: string | null;
  createdAt: string;
  shippingAddress: CustomerShippingAddress | null;
  insuredUsd: number;
};

export type CustomerHistoryPage = {
  data: CustomerHistoryOrder[];
  total: number;
  nextCursor: string | null;
  dataAsOf: string | null;
};

export type CustomerSearchBody = { email: string } | { phone: string };

export type CustomerSearchResult = {
  customerId: string;
  storeId: string;
  storeName: string | null;
  email: string | null;
  name: string | null;
  lastOrderAt: string;
};

// insuredOrderCount arrives with slice 37 and total with slice 38, so a missing one reads as unknown.
export type CustomerListItem = {
  customerId: string;
  storeId: string;
  storeName: string | null;
  email: string | null;
  name: string | null;
  orderCount: number;
  claimCount: number;
  deniedClaimCount: number;
  insuredOrderCount?: number;
  protectionSpendUsd: number;
  reimbursedUsd: number;
  insuredUsd: number;
  addressCount: number;
  lastOrderAt: string | null;
  lastClaimAt: string | null;
  sharedIdentity: boolean;
};

// Mirrors analytics-svc's sort keys; a cursor only pages the sort and direction it was issued for.
export enum CustomerListSort {
  OrderCount = 'order_count',
  ClaimCount = 'claim_count',
  ProtectedOrderCount = 'protected_order_count',
  ProtectionSpendUsd = 'total_protection_spend_usd',
  ReimbursedUsd = 'total_reimbursed_usd',
  InsuredValueUsd = 'total_insured_value_usd',
  AddressCount = 'distinct_address_count',
}

export enum CustomerListDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export type CustomerListOrder = { sort: CustomerListSort; direction: CustomerListDirection };

export enum CustomerListBound {
  Min = 'min',
  Max = 'max',
}

// Sent as an inclusive min_<sort> or max_<sort>; a cursor only pages the filter set it was issued for.
export type CustomerListFilter = { sort: CustomerListSort; bound: CustomerListBound; value: number };

export type CustomerListPage = {
  data: CustomerListItem[];
  nextCursor: string | null;
  total?: number;
  dataAsOf: string | null;
  maxLagHours: number;
};

type CustomerAddressField = 'address1' | 'address2' | 'city' | 'stateAbr' | 'zip' | 'countryAbr';

// Display fields come from the newest order shipped to the address; claimCount counts orders with a claim.
export type CustomerAddress = Record<CustomerAddressField, string | null> & {
  storeId: string;
  storeName: string | null;
  orderCount: number;
  claimCount: number;
  firstUsedAt: string;
  lastUsedAt: string;
};

// data is last used first and capped, while total counts every distinct address like the tile.
export type CustomerAddressesResponse = { data: CustomerAddress[]; total: number; dataAsOf: string | null };

export default class AnalyticsRestService extends GatewayBase {
  // Uncached because the cache key omits X-Store-Id; a body without customerId (a codeless-401 envelope) errors.
  getCustomer(
    customerId: string,
    storeIds: string[],
    include?: CustomerDetailInclude,
  ): Observable<CustomerDetailResponse> {
    return this.client
      .get<CustomerDetailResponse>(
        `v1/analytics/customers/${encodeURIComponent(customerId)}`,
        {
          params: { store_id: storeIds, ...(include && { include }) },
          paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }),
        },
        { noCache: true },
      )
      .pipe(
        map(res => {
          const body = res as unknown as CustomerDetailResponse | null;
          if (!body?.customerId) throw new Error('Customer unavailable');

          return body;
        }),
      );
  }

  // Uncached for the same X-Store-Id reason; several stores repeat store_id and interleave newest first.
  getCustomerOrders(customerId: string, storeIds: string | string[], cursor?: string): Observable<CustomerHistoryPage> {
    return this.client
      .get<CustomerHistoryPage>(
        `v1/analytics/customers/${encodeURIComponent(customerId)}/orders`,
        {
          params: { store_id: storeIds, ...(cursor && { cursor }) },
          paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }),
        },
        { noCache: true },
      )
      .pipe(
        map(res => {
          const body = res as unknown as CustomerHistoryPage | null;
          if (!Array.isArray(body?.data)) throw new Error('Order history unavailable');

          return body;
        }),
      );
  }

  // Uncached for the same X-Store-Id reason; no storeIds sends no store_id, which only OP staff may do.
  listCustomers(
    storeIds: string[],
    { direction, sort }: CustomerListOrder,
    limit: number,
    cursor?: string,
    filters: CustomerListFilter[] = [],
  ): Observable<CustomerListPage> {
    const bounds = Object.fromEntries(filters.map(filter => [`${filter.bound}_${filter.sort}`, String(filter.value)]));

    return this.client
      .get<CustomerListPage>(
        'v1/analytics/customers',
        {
          params: { direction, sort, store_id: storeIds, ...bounds, ...(cursor && { cursor }), limit },
          paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }),
        },
        { noCache: true },
      )
      .pipe(
        map(res => {
          const body = res as unknown as CustomerListPage | null;
          if (!Array.isArray(body?.data)) throw new Error('Customer list unavailable');

          return body;
        }),
      );
  }

  // POST keeps the email or phone out of URLs the gateway logs and never caches; no storeIds sends no store_id (every store).
  searchCustomers(body: CustomerSearchBody, storeIds: string[]): Observable<CustomerSearchResult[]> {
    return this.client
      .post<
        CustomerSearchResult[]
      >('v1/analytics/customers/search', body, { params: { store_id: storeIds }, paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }) })
      .pipe(
        map(res => {
          if (!Array.isArray(res?.data)) throw new Error('Customer search unavailable');

          return res.data;
        }),
      );
  }

  // Uncached for the same X-Store-Id reason; several stores repeat store_id and list each store's addresses.
  getCustomerAddresses(customerId: string, storeIds: string[]): Observable<CustomerAddressesResponse> {
    return this.client
      .get<CustomerAddressesResponse>(
        `v1/analytics/customers/${encodeURIComponent(customerId)}/addresses`,
        {
          params: { store_id: storeIds },
          paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }),
        },
        { noCache: true },
      )
      .pipe(
        map(res => {
          const body = res as unknown as CustomerAddressesResponse | null;
          if (!Array.isArray(body?.data)) throw new Error('Customer addresses unavailable');

          return body;
        }),
      );
  }

  getEmbeddedStatus(sessionToken: string): Observable<EmbeddedStatusResponse> {
    return this.client
      .get<EmbeddedStatusResponse>('v1/shopify/analytics/embedded/status', {
        headers: { Authorization: `Bearer ${sessionToken}` },
      })
      .pipe(
        // The /embedded/status controller returns the bare object; other endpoints
        // wrap with ResponseInterceptor as { data: ... }. Unwrap defensively so
        // callers always see EmbeddedStatusResponse.
        map(res => ((res as unknown as { data?: EmbeddedStatusResponse })?.data ?? res) as EmbeddedStatusResponse),
      );
  }

  getEmbeddedStoreMetrics(sessionToken: string) {
    return this.client.get<StoreMetricsResponse>('v1/shopify/analytics/embedded/store-metrics', {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
  }

  getEmbeddedCachedMetrics(sessionToken: string) {
    return this.client.get<StoreMetricsResponse>('v1/shopify/analytics/embedded/cached-metrics', {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
  }
}
